import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { verificationAPI } from '@/lib/api';
import { MessageCircle, Check, ArrowRight, ExternalLink, Copy, Phone, Shield } from 'lucide-react';

// WhatsApp Business Logo SVG
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const WhatsAppVerify = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState(user.phone || '');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [whatsappLink, setWhatsappLink] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [businessNumber, setBusinessNumber] = useState('');

  const handleSendWhatsApp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await verificationAPI.sendWhatsApp(phone);
      setWhatsappLink(response.data.whatsapp_link);
      setVerificationCode(response.data.verification_code);
      setBusinessNumber(response.data.business_number || '+61452401418');
      toast.success('Verification ready! Open WhatsApp to continue.');
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate verification');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await verificationAPI.verifyWhatsApp(phone, code);
      if (response.data.valid) {
        toast.success('Phone verified successfully!');
        const updatedUser = { ...user, verified_phone: true, phone };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setTimeout(() => {
          navigate(user.role === 'buyer' ? '/buyer/dashboard' : '/seller/dashboard');
        }, 1500);
      } else {
        toast.error('Invalid verification code');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(verificationCode);
    toast.success('Code copied to clipboard!');
  };

  const openWhatsApp = () => {
    window.open(whatsappLink, '_blank');
  };

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-[#25D366] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <WhatsAppIcon className="w-10 h-10 text-white" />
          </div>
          <h1 data-testid="whatsapp-verify-heading" className="text-3xl font-chivo font-bold text-slate-900 mb-2">
            {step === 1 ? 'WhatsApp Verification' : 'Complete Verification'}
          </h1>
          <p className="text-slate-600">
            {step === 1 
              ? "Verify your phone via our WhatsApp Business" 
              : 'Send the message and enter your code'}
          </p>
        </div>

        {step === 1 ? (
          <form data-testid="send-whatsapp-form" onSubmit={handleSendWhatsApp} className="space-y-6">
            <div>
              <Label htmlFor="phone" className="text-base font-semibold">Your Phone Number</Label>
              <Input
                data-testid="whatsapp-phone-input"
                id="phone"
                type="tel"
                placeholder="+61 400 000 000"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                className="mt-2 text-lg"
              />
              <p className="text-xs text-slate-500 mt-2">
                Include country code (e.g., +61 for Australia, +675 for PNG)
              </p>
            </div>

            {/* Business WhatsApp Info */}
            <div className="bg-[#25D366]/10 p-4 rounded-xl border border-[#25D366]/30">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-[#25D366] rounded-full flex items-center justify-center">
                  <WhatsAppIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">ServPac</p>
                  <p className="text-sm text-slate-600 flex items-center">
                    <Shield className="w-3 h-3 mr-1 text-blue-500" />
                    Meta Verified Business
                  </p>
                </div>
              </div>
              <p className="text-sm text-slate-600">
                You'll be connected to our official WhatsApp Business to verify your phone number securely.
              </p>
            </div>
            
            <div className="bg-slate-50 p-4 rounded-lg">
              <h4 className="font-medium text-slate-800 mb-2">How it works:</h4>
              <ol className="text-sm text-slate-600 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#25D366] text-white flex items-center justify-center text-xs flex-shrink-0">1</span>
                  Enter your phone number above
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#25D366] text-white flex items-center justify-center text-xs flex-shrink-0">2</span>
                  Click to open WhatsApp and send the message
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#25D366] text-white flex items-center justify-center text-xs flex-shrink-0">3</span>
                  Our team confirms and you enter your code
                </li>
              </ol>
            </div>

            <Button
              data-testid="send-whatsapp-btn"
              type="submit"
              className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white h-12 text-base"
              disabled={loading || !phone}
            >
              {loading ? (
                'Generating...'
              ) : (
                <>
                  <WhatsAppIcon className="w-5 h-5 mr-2" />
                  Continue with WhatsApp
                </>
              )}
            </Button>
          </form>
        ) : (
          <div className="space-y-6">
            {/* Business Number Display */}
            <div className="bg-[#25D366]/10 p-4 rounded-xl text-center border border-[#25D366]/30">
              <p className="text-sm text-slate-600 mb-1">ServPac WhatsApp Business</p>
              <p className="text-xl font-bold text-[#128C7E] flex items-center justify-center">
                <Phone className="w-5 h-5 mr-2" />
                {businessNumber}
              </p>
            </div>

            {/* Verification Code Display */}
            <div className="bg-amber-50 p-4 rounded-xl text-center border border-amber-200">
              <p className="text-sm text-amber-700 mb-2 font-medium">Your Verification Code</p>
              <div className="flex items-center justify-center gap-3">
                <span className="text-4xl font-mono font-bold text-amber-800 tracking-[0.3em]">
                  {verificationCode}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={copyCode}
                  className="text-amber-600 hover:text-amber-800"
                >
                  <Copy className="w-5 h-5" />
                </Button>
              </div>
              <p className="text-xs text-amber-600 mt-2">Expires in 10 minutes</p>
            </div>

            {/* WhatsApp Link Button */}
            <Button
              data-testid="open-whatsapp-btn"
              type="button"
              className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white h-14 text-base"
              onClick={openWhatsApp}
            >
              <WhatsAppIcon className="w-6 h-6 mr-3" />
              Open WhatsApp & Send Message
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>

            <div className="text-center">
              <p className="text-sm text-slate-500">
                After sending the message, enter your code below
              </p>
            </div>

            {/* Code Entry Form */}
            <form data-testid="verify-whatsapp-form" onSubmit={handleVerify} className="space-y-4">
              <div>
                <Label htmlFor="code" className="text-base font-semibold">Enter Verification Code</Label>
                <Input
                  data-testid="whatsapp-code-input"
                  id="code"
                  type="text"
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  maxLength={6}
                  className="text-center text-3xl tracking-[0.5em] h-14 font-mono mt-2"
                />
              </div>
              <Button
                data-testid="verify-whatsapp-btn"
                type="submit"
                className="w-full h-12"
                disabled={loading || code.length !== 6}
              >
                {loading ? 'Verifying...' : (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Verify My Number
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>

            <Button
              data-testid="resend-whatsapp-btn"
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => {
                setStep(1);
                setWhatsappLink('');
                setVerificationCode('');
                setCode('');
              }}
            >
              ← Start Over
            </Button>
          </div>
        )}

        <div className="mt-6 text-center">
          <Button
            data-testid="skip-verification-btn"
            variant="ghost"
            size="sm"
            onClick={() => navigate(user.role === 'buyer' ? '/buyer/dashboard' : '/seller/dashboard')}
          >
            Skip for now
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default WhatsAppVerify;
